import { z } from 'zod/v4';
// Base schema for roll call status
const rollCallStatusBaseSchema = z.object({
    meetingMemberId: z.number('Meeting member ID is required'),
    isPresent: z.boolean('Presence status is required'),
    notes: z.string().nullable().optional(),
});
// Schema for creating roll call statuses
export const rollCallStatusCreateSchema = rollCallStatusBaseSchema
    .extend({
    isAbsenceExcused: z.boolean().nullable().optional(),
    isPresencePhysical: z.boolean().nullable().optional(),
})
    .superRefine((data, ctx) => {
    // Validate absence type when status is absent
    if (!data.isPresent) {
        if (data.isAbsenceExcused === null || data.isAbsenceExcused === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Absence excused status is required when member is absent',
                path: ['isAbsenceExcused'],
            });
        }
        if (data.isPresencePhysical !== null && data.isPresencePhysical !== undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Physical presence status should not be set when member is absent',
                path: ['isPresencePhysical'],
            });
        }
    }
    // Validate participation type when status is present
    if (data.isPresent) {
        if (data.isAbsenceExcused !== null && data.isAbsenceExcused !== undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Absence excused status should not be set when member is present',
                path: ['isAbsenceExcused'],
            });
        }
        if (data.isPresencePhysical === null || data.isPresencePhysical === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Physical presence status is required when member is present',
                path: ['isPresencePhysical'],
            });
        }
    }
});
// Schema for creating a roll call
export const rollCallCreateSchema = z.object({
    isRecorded: z.boolean().optional().default(false),
    statuses: z.array(rollCallStatusCreateSchema).min(1, 'At least one status is required'),
});
// Schema for updating a roll call
export const rollCallUpdateSchema = z.object({
    isRecorded: z.boolean().optional(),
});
// Schema for updating roll call statuses
export const rollCallStatusesUpdateSchema = z.object({
    statuses: z.array(rollCallStatusCreateSchema).min(1, 'At least one status is required'),
});
/**
 * Schema for creating a roll call from the minutes editor. Body scope is resolved
 * server-side from the entry, so the payload only carries presence statuses and the
 * optional draft/recorded flag — no `meetingBodyId`. Joint meetings produce one
 * `RollCall` per body in the entry's body scope, all sharing the same `orderById` slot.
 */
export const minutesRollCallCreateSchema = z.object({
    isRecorded: z.boolean().optional().default(false),
    statuses: z.array(rollCallStatusCreateSchema).min(1, 'At least one status is required'),
    recorded: z
        .boolean()
        .optional()
        .describe('True for live-console origin (stamps recordedAt); omitted leaves the roll call staged.'),
});
/**
 * Schema for updating a roll call group's recorded flag from the minutes editor. The flag
 * is applied to every peer row in the group atomically.
 */
export const minutesRollCallGroupUpdateSchema = z.object({
    isRecorded: z.boolean().optional(),
    recorded: z
        .boolean()
        .optional()
        .describe('True records a staged roll call (stamps recordedAt on every peer once); idempotent.'),
});
